import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CampaignIcon from '@mui/icons-material/Campaign';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useCompanyAlerts } from '../hooks/useCompanyAlerts';
import { usePermissions } from '../hooks/usePermissions';
import {
  parseAlertDate,
  isAlertDismissed,
} from '../lib/userAlerts';
import { canViewAlertForUser } from '../lib/alertModuleAccess';
import {
  isInboxItemAnnouncement,
  isInboxItemRead,
  markAnnouncementAsSeen,
  parseAnnouncementTimestamp,
} from '../lib/announcements';
import {
  resolveNotificationClickAction,
} from '../lib/notificationRouting';
import { dashboardTicketPath } from '../lib/dashboardRoutes';
import AlertLivenessSourcesSettings from './AlertLivenessSourcesSettings';
import AlertDetailView from './AlertDetailView';
import NotificationMessageModal from './notifications/NotificationMessageModal';

const BATCH_SIZE = 450;

function severityColor(sev) {
  if (sev === 'critical') return 'error';
  if (sev === 'warning') return 'warning';
  if (sev === 'success') return 'success';
  return 'info';
}

async function commitBatchedUpdates(updates) {
  for (let i = 0; i < updates.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    updates.slice(i, i + BATCH_SIZE).forEach(({ id, data }) => {
      batch.update(doc(db, 'alerts', id), data);
    });
    await batch.commit();
  }
}

function canHardDeleteAlert(userData, item) {
  if (!userData || !item?.id) return false;
  if (userData.role === 'super_admin') return true;
  if (!['company_admin', 'developer'].includes(userData.role)) return false;
  if (!userData.companyId || item.targetCompanyId !== userData.companyId) return false;
  if (item.targetType !== 'company' && item.targetType !== 'company_role') return false;
  return true;
}

function canConfigureLivenessSources(userData) {
  return Boolean(
    userData?.companyId && ['company_admin', 'developer'].includes(userData?.role),
  );
}

function canOpenLinkedTicket(userData, item, checkPermission) {
  const tid = item?.meta?.ticketId;
  if (!tid || typeof tid !== 'string') return false;
  if (userData?.role === 'super_admin') return true;
  return Boolean(userData?.companyId && checkPermission('ticketing', 'read'));
}

/**
 * @param {object} [alertsRemote] — from useCompanyAlerts (parent); avoids duplicate fetches
 * @param {'default'|'menu'} [variant] — menu: tighter layout for popover
 * @param {number} [maxItems] — cap list length (e.g. in toolbar menu)
 * @param {Function} [onOpenMessageModal] — parent-owned modal (bell menu popover)
 */
export default function AlertsInbox({
  userData,
  showTitle = true,
  variant = 'default',
  maxItems,
  alertsRemote,
  focusedAlertId = null,
  onFocusedAlertChange,
  onOpenedFromMenu,
  onCreateTicketFromAlert,
  onOpenMessageModal,
}) {
  const internal = useCompanyAlerts(userData, { skip: Boolean(alertsRemote) });
  const remote = alertsRemote;
  const { checkPermission } = usePermissions(userData);
  const rows = remote?.rows ?? internal.rows;
  const loading = remote?.loading ?? internal.loading;
  const refreshAlerts = remote?.refresh ?? internal.refresh;

  const isSuperAdmin = userData?.role === 'super_admin';

  const canViewAlert = (item) => {
    if (isInboxItemAnnouncement(item)) return true;
    return isSuperAdmin || canViewAlertForUser(item, userData, checkPermission);
  };

  const itemCreatedDate = (item) => {
    if (isInboxItemAnnouncement(item)) {
      return parseAnnouncementTimestamp(item.timestamp ?? item.createdAt);
    }
    return parseAlertDate(item.createdAt);
  };

  const markAnnouncementRead = async (itemId) => {
    if (!uid || !itemId) return;
    try {
      await markAnnouncementAsSeen(db, itemId, uid);
      closeMenu();
      await refreshAlerts();
    } catch (e) {
      console.error('markAnnouncementRead:', e);
      setError(e.message || 'Could not mark announcement as read.');
    }
  };

  const markNotificationReadIfNeeded = async (item) => {
    if (!uid || !item?.id || isInboxItemRead(item, uid)) return;
    try {
      if (isInboxItemAnnouncement(item)) {
        await markAnnouncementAsSeen(db, item.id, uid);
      } else {
        await updateDoc(doc(db, 'alerts', item.id), {
          [`readBy.${uid}`]: serverTimestamp(),
        });
      }
      await refreshAlerts();
    } catch (e) {
      console.error('markNotificationRead:', e);
      setError(e.message || 'Could not mark notification as read.');
    }
  };

  const handleRowOpen = async (item) => {
    await markNotificationReadIfNeeded(item);

    const action = resolveNotificationClickAction(item);

    if (action.kind === 'ticket') {
      const ticketItem = {
        ...item,
        meta: { ...(item.meta || {}), ticketId: action.ticketId },
      };
      if (!canOpenLinkedTicket(userData, ticketItem, checkPermission)) {
        if (isMenu) {
          onOpenedFromMenu?.();
          setFocus(item.id);
        } else {
          setFocus(item.id);
        }
        return;
      }
      if (isMenu) onOpenedFromMenu?.();
      if (userData?.role === 'super_admin') {
        navigate('/admin', { state: { activeTab: 'Tickets', openTicketId: action.ticketId } });
        return;
      }
      navigate(dashboardTicketPath(action.ticketId));
      return;
    }

    if (action.kind === 'modal') {
      if (isMenu) onOpenedFromMenu?.();
      if (onOpenMessageModal) {
        onOpenMessageModal(item);
      } else {
        setMessageModalItem(item);
      }
      return;
    }

    if (isMenu) {
      onOpenedFromMenu?.();
      setFocus(item.id);
      return;
    }

    if (isInboxItemAnnouncement(item)) return;
    setFocus(item.id);
  };

  const [error, setError] = useState('');
  const [bulkWorking, setBulkWorking] = useState(false);
  const [listTab, setListTab] = useState('inbox');
  const [readFilter, setReadFilter] = useState('all');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmRestoreAllOpen, setConfirmRestoreAllOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteWorking, setDeleteWorking] = useState(false);
  const [confirmDeleteAllArchivedOpen, setConfirmDeleteAllArchivedOpen] = useState(false);
  const [messageModalItem, setMessageModalItem] = useState(null);

  const navigate = useNavigate();
  const uid = auth.currentUser?.uid;
  const isMenu = variant === 'menu';
  const setFocus = onFocusedAlertChange || (() => {});

  const inboxRows = useMemo(
    () => rows.filter((r) => isInboxItemAnnouncement(r) || !isAlertDismissed(r, uid)),
    [rows, uid],
  );

  const archivedRows = useMemo(() => {
    if (!uid) return [];
    return rows.filter((r) => isAlertDismissed(r, uid));
  }, [rows, uid]);

  const detailItem = useMemo(
    () => (focusedAlertId ? rows.find((r) => r.id === focusedAlertId) : null),
    [rows, focusedAlertId],
  );

  const navPool = useMemo(() => {
    if (!detailItem || !uid) return [];
    return isAlertDismissed(detailItem, uid) ? archivedRows : inboxRows;
  }, [detailItem, uid, archivedRows, inboxRows]);

  const navIdx = useMemo(
    () => navPool.findIndex((r) => r.id === focusedAlertId),
    [navPool, focusedAlertId],
  );

  const lastSyncedFocus = useRef(null);
  useEffect(() => {
    if (!focusedAlertId) {
      lastSyncedFocus.current = null;
      return;
    }
    if (lastSyncedFocus.current === focusedAlertId) return;
    const found = rows.find((r) => r.id === focusedAlertId);
    if (!found) return;
    lastSyncedFocus.current = focusedAlertId;
    setListTab(isAlertDismissed(found, uid) ? 'archived' : 'inbox');
  }, [focusedAlertId, rows, uid]);

  useEffect(() => {
    if (!focusedAlertId || loading) return;
    if (!rows.some((r) => r.id === focusedAlertId)) {
      setFocus(null);
    }
  }, [focusedAlertId, rows, loading, setFocus]);

  const canCreateTicketFromAlert = Boolean(
    onCreateTicketFromAlert &&
      checkPermission('ticketing', 'write') &&
      userData?.companyId &&
      ['company_admin', 'developer', 'manager', 'agent', 'trainee'].includes(userData?.role),
  );

  const deletableArchivedRows = useMemo(
    () => archivedRows.filter((it) => canHardDeleteAlert(userData, it)),
    [archivedRows, userData],
  );

  const unreadInInbox = useMemo(() => {
    if (!uid) return 0;
    return inboxRows.filter((it) => !isInboxItemRead(it, uid)).length;
  }, [inboxRows, uid]);

  const visibleItems = useMemo(() => {
    const pool = listTab === 'inbox' ? inboxRows : archivedRows;
    if (readFilter === 'unread') {
      return pool.filter((it) => !isInboxItemRead(it, uid));
    }
    if (readFilter === 'read') {
      return pool.filter((it) => isInboxItemRead(it, uid));
    }
    return pool;
  }, [listTab, inboxRows, archivedRows, readFilter, uid]);

  const displayedItems = useMemo(() => {
    const items = visibleItems.filter((item) => canViewAlertForUser(item, userData, checkPermission));
    if (isMenu && maxItems != null && items.length > maxItems) {
      return items.slice(0, maxItems);
    }
    return items;
  }, [isMenu, maxItems, visibleItems, userData, checkPermission]);

  const truncated = isMenu && maxItems != null && visibleItems.length > displayedItems.length;

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const openLinkedTicket = (item) => {
    const tid = item?.meta?.ticketId;
    if (!tid || typeof tid !== 'string') return;
    closeMenu();
    setFocus(null);
    if (userData?.role === 'super_admin') {
      navigate('/admin', { state: { activeTab: 'Tickets', openTicketId: tid } });
      return;
    }
    navigate(dashboardTicketPath(tid));
  };

  const markRead = async (itemId) => {
    if (!uid) return;
    const row = rows.find((r) => r.id === itemId);
    if (row && isInboxItemAnnouncement(row)) {
      await markAnnouncementRead(itemId);
      return;
    }
    try {
      await updateDoc(doc(db, 'alerts', itemId), {
        [`readBy.${uid}`]: serverTimestamp(),
      });
      closeMenu();
      await refreshAlerts();
    } catch (e) {
      console.error('markRead:', e);
      setError(e.message || 'Could not mark alert as read.');
    }
  };

  /** Opening an alert in the full-page detail view counts as reading it. */
  useEffect(() => {
    if (!uid || !focusedAlertId || isMenu) return;
    const it = rows.find((r) => r.id === focusedAlertId);
    if (!it || loading || isInboxItemAnnouncement(it)) return;
    if (isInboxItemRead(it, uid)) return;
    let cancelled = false;
    (async () => {
      try {
        await updateDoc(doc(db, 'alerts', focusedAlertId), {
          [`readBy.${uid}`]: serverTimestamp(),
        });
        if (!cancelled) await refreshAlerts();
      } catch (e) {
        console.error('autoMarkAlertRead:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [focusedAlertId, uid, isMenu, loading, rows, refreshAlerts]);

  const markUnread = async (itemId) => {
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'alerts', itemId), {
        [`readBy.${uid}`]: deleteField(),
      });
      closeMenu();
      await refreshAlerts();
    } catch (e) {
      console.error('markUnread:', e);
      setError(e.message || 'Could not mark alert as unread.');
    }
  };

  const dismissOne = async (itemId) => {
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'alerts', itemId), {
        [`dismissedBy.${uid}`]: serverTimestamp(),
      });
      closeMenu();
      await refreshAlerts();
    } catch (e) {
      console.error('dismissOne:', e);
      setError(e.message || 'Could not archive alert.');
    }
  };

  const restoreOne = async (itemId) => {
    if (!uid) return;
    try {
      await updateDoc(doc(db, 'alerts', itemId), {
        [`dismissedBy.${uid}`]: deleteField(),
      });
      closeMenu();
      await refreshAlerts();
    } catch (e) {
      console.error('restoreOne:', e);
      setError(e.message || 'Could not restore alert.');
    }
  };

  const markAllRead = async () => {
    if (!uid) return;
    const targets = inboxRows.filter((it) => !isInboxItemRead(it, uid));
    if (!targets.length) return;
    setBulkWorking(true);
    setError('');
    try {
      await Promise.all(
        targets.map((it) => {
          if (isInboxItemAnnouncement(it)) {
            return markAnnouncementAsSeen(db, it.id, uid);
          }
          return updateDoc(doc(db, 'alerts', it.id), {
            [`readBy.${uid}`]: serverTimestamp(),
          });
        }),
      );
      await refreshAlerts();
    } catch (e) {
      console.error('markAllRead:', e);
      setError(e.message || 'Could not mark all as read.');
    } finally {
      setBulkWorking(false);
    }
  };

  const markAllUnread = async () => {
    if (!uid) return;
    const targets = inboxRows.filter((it) => !isInboxItemAnnouncement(it) && isInboxItemRead(it, uid));
    if (!targets.length) return;
    setBulkWorking(true);
    setError('');
    try {
      const updates = targets.map((it) => ({
        id: it.id,
        data: { [`readBy.${uid}`]: deleteField() },
      }));
      await commitBatchedUpdates(updates);
      await refreshAlerts();
    } catch (e) {
      console.error('markAllUnread:', e);
      setError(e.message || 'Could not mark all as unread.');
    } finally {
      setBulkWorking(false);
    }
  };

  const dismissAllInbox = async () => {
    if (!uid) return;
    const targets = inboxRows.filter((it) => !isInboxItemAnnouncement(it));
    if (!targets.length) return;
    setBulkWorking(true);
    setError('');
    setConfirmClearOpen(false);
    try {
      const updates = targets.map((it) => ({
        id: it.id,
        data: { [`dismissedBy.${uid}`]: serverTimestamp() },
      }));
      await commitBatchedUpdates(updates);
      await refreshAlerts();
      setListTab('archived');
    } catch (e) {
      console.error('dismissAllInbox:', e);
      setError(e.message || 'Could not clear inbox.');
    } finally {
      setBulkWorking(false);
    }
  };

  const restoreAllArchived = async () => {
    if (!uid) return;
    const targets = archivedRows;
    if (!targets.length) return;
    setBulkWorking(true);
    setError('');
    setConfirmRestoreAllOpen(false);
    try {
      const updates = targets.map((it) => ({
        id: it.id,
        data: { [`dismissedBy.${uid}`]: deleteField() },
      }));
      await commitBatchedUpdates(updates);
      await refreshAlerts();
      setListTab('inbox');
    } catch (e) {
      console.error('restoreAllArchived:', e);
      setError(e.message || 'Could not restore alerts.');
    } finally {
      setBulkWorking(false);
    }
  };

  const openRowMenu = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  const deleteAlertPermanent = async (alertId) => {
    await deleteDoc(doc(db, 'alerts', alertId));
  };

  const handleConfirmPermanentDelete = async () => {
    if (!deleteTarget?.id) return;
    const deletedId = deleteTarget.id;
    setDeleteWorking(true);
    setError('');
    try {
      await deleteAlertPermanent(deletedId);
      setConfirmDeleteOpen(false);
      setDeleteTarget(null);
      closeMenu();
      if (focusedAlertId && focusedAlertId === deletedId) {
        setFocus(null);
      }
      await refreshAlerts();
    } catch (e) {
      console.error('deleteAlert:', e);
      setError(e.message || 'Could not delete alert.');
    } finally {
      setDeleteWorking(false);
    }
  };

  const handleDeleteAllArchivedPermanent = async () => {
    if (!deletableArchivedRows.length) return;
    setBulkWorking(true);
    setError('');
    setConfirmDeleteAllArchivedOpen(false);
    try {
      const targets = deletableArchivedRows;
      for (let i = 0; i < targets.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        targets.slice(i, i + BATCH_SIZE).forEach((it) => {
          batch.delete(doc(db, 'alerts', it.id));
        });
        await batch.commit();
      }
      await refreshAlerts();
    } catch (e) {
      console.error('deleteAllArchived:', e);
      setError(e.message || 'Could not delete all archived alerts.');
    } finally {
      setBulkWorking(false);
    }
  };

  const emptyMessage = () => {
    if (loading) return null;
    if (rows.length === 0) {
      return 'No notifications for your role right now.';
    }
    if (listTab === 'inbox' && inboxRows.length === 0) {
      return 'Inbox is empty. Cleared notifications appear under Archived.';
    }
    if (listTab === 'archived' && archivedRows.length === 0) {
      return 'Nothing in archived yet. Use “Clear inbox” or archive a notification to move it here.';
    }
    if (visibleItems.length === 0) {
      return readFilter === 'unread'
        ? 'No unread notifications in this view.'
        : 'No read notifications in this view.';
    }
    return null;
  };

  const emptyText = emptyMessage();

  const cardContentPy = isMenu ? 1 : 1.5;

  return (
    <Box sx={{ mt: showTitle && !isMenu ? 3 : 0 }}>
      {!isMenu && focusedAlertId && detailItem && canViewAlert(detailItem) && !isInboxItemAnnouncement(detailItem) && (
        <AlertDetailView
          item={detailItem}
          uid={uid}
          navIndex={Math.max(0, navIdx)}
          navTotal={navPool.length}
          hasPrev={navIdx > 0}
          hasNext={navIdx >= 0 && navIdx < navPool.length - 1}
          onBack={() => setFocus(null)}
          onPrev={() => {
            if (navIdx > 0) setFocus(navPool[navIdx - 1].id);
          }}
          onNext={() => {
            if (navIdx >= 0 && navIdx < navPool.length - 1) setFocus(navPool[navIdx + 1].id);
          }}
          onMarkRead={markRead}
          onMarkUnread={markUnread}
          onArchive={dismissOne}
          onRestore={restoreOne}
          onDeletePermanent={(it) => {
            setDeleteTarget(it);
            setConfirmDeleteOpen(true);
          }}
          canHardDelete={canHardDeleteAlert(userData, detailItem)}
          onCreateTicket={() => onCreateTicketFromAlert?.(detailItem)}
          canCreateTicket={canCreateTicketFromAlert}
          onOpenLinkedTicket={openLinkedTicket}
          canOpenTicket={canOpenLinkedTicket(userData, detailItem, checkPermission)}
        />
      )}
      {!isMenu && canConfigureLivenessSources(userData) && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              What can send notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Automated monitors post here when status changes. Turn off noisy sources or choose whether “back to LIVE”
              should notify everyone.
            </Typography>
            <AlertLivenessSourcesSettings userData={userData} />
          </CardContent>
        </Card>
      )}
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        spacing={isMenu ? 0.5 : 1}
        useFlexGap
        sx={{ mb: isMenu ? 1 : 1.5 }}
        justifyContent="space-between"
      >
        <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={isMenu ? 0.5 : 1} useFlexGap>
          {showTitle && !isMenu && (
            <>
              <NotificationsActiveIcon color="primary" />
              <Typography variant="h6">Notifications</Typography>
            </>
          )}
          {isMenu && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 0.5 }}>
              Notifications
            </Typography>
          )}
          <Chip
            size="small"
            label={uid ? `${unreadInInbox} unread` : 'Sign in'}
            color={unreadInInbox > 0 ? 'warning' : 'default'}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={listTab}
            onChange={(_, v) => v && setListTab(v)}
            aria-label="Inbox or archived"
            sx={isMenu ? { '& .MuiToggleButton-root': { px: 1, py: 0.25, fontSize: 12 } } : undefined}
          >
            <ToggleButton value="inbox">Inbox</ToggleButton>
            <ToggleButton value="archived">Archived</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={readFilter}
            onChange={(_, v) => v && setReadFilter(v)}
            aria-label="Read filter"
            sx={isMenu ? { '& .MuiToggleButton-root': { px: 0.75, py: 0.25, fontSize: 12 } } : undefined}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="unread">Unread</ToggleButton>
            <ToggleButton value="read">Read</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" flexWrap="wrap" spacing={0.75} useFlexGap alignItems="center">
          {listTab === 'inbox' && uid && (
            <>
              {isMenu ? (
                <>
                  <Tooltip title="Mark all read">
                    <span>
                      <IconButton
                        size="small"
                        disabled={bulkWorking || inboxRows.every((it) => isInboxItemRead(it, uid))}
                        onClick={markAllRead}
                        aria-label="Mark all read"
                      >
                        <DoneAllIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Mark all unread">
                    <span>
                      <IconButton
                        size="small"
                        disabled={bulkWorking || inboxRows.every((it) => !isInboxItemRead(it, uid))}
                        onClick={markAllUnread}
                        aria-label="Mark all unread"
                      >
                        <MarkEmailUnreadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Clear inbox">
                    <span>
                      <IconButton
                        size="small"
                        color="warning"
                        disabled={bulkWorking || inboxRows.length === 0}
                        onClick={() => setConfirmClearOpen(true)}
                        aria-label="Clear inbox"
                      >
                        <ClearAllIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title="Mark every inbox notification as read">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DoneAllIcon />}
                        disabled={bulkWorking || inboxRows.every((it) => isInboxItemRead(it, uid))}
                        onClick={markAllRead}
                      >
                        Mark all read
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Mark every inbox notification as unread">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MarkEmailUnreadIcon />}
                        disabled={bulkWorking || inboxRows.every((it) => !isInboxItemRead(it, uid))}
                        onClick={markAllUnread}
                      >
                        Mark all unread
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move all inbox notifications to Archived">
                    <span>
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        startIcon={<ClearAllIcon />}
                        disabled={bulkWorking || inboxRows.length === 0}
                        onClick={() => setConfirmClearOpen(true)}
                      >
                        Clear inbox
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </>
          )}
          {listTab === 'archived' && uid && (
            <Tooltip title="Return every archived notification to Inbox">
              <span>
                {isMenu ? (
                  <IconButton
                    size="small"
                    disabled={bulkWorking || archivedRows.length === 0}
                    onClick={() => setConfirmRestoreAllOpen(true)}
                    aria-label="Restore all"
                  >
                    <UnarchiveIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UnarchiveIcon />}
                    disabled={bulkWorking || archivedRows.length === 0}
                    onClick={() => setConfirmRestoreAllOpen(true)}
                  >
                    Restore all
                  </Button>
                )}
              </span>
            </Tooltip>
          )}
          {listTab === 'archived' && uid && deletableArchivedRows.length > 0 && (
            <Tooltip
              title={
                isMenu
                  ? `Permanently delete ${deletableArchivedRows.length} archived notification(s) you can remove (cannot undo)`
                  : 'Permanently delete archived notifications you are allowed to remove (cannot undo)'
              }
            >
              <span>
                {isMenu ? (
                  <IconButton
                    size="small"
                    color="error"
                    disabled={bulkWorking}
                    onClick={() => setConfirmDeleteAllArchivedOpen(true)}
                    aria-label={`Purge ${deletableArchivedRows.length} archived alerts`}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteForeverIcon />}
                    disabled={bulkWorking}
                    onClick={() => setConfirmDeleteAllArchivedOpen(true)}
                  >
                    Purge archived ({deletableArchivedRows.length})
                  </Button>
                )}
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!loading && emptyText && (
        <Alert severity="info" sx={{ mb: isMenu ? 1 : 2 }}>{emptyText}</Alert>
      )}

      {truncated && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Showing {displayedItems.length} of {visibleItems.length}. Open Notifications for the full list.
        </Typography>
      )}

      <Stack spacing={isMenu ? 0.75 : 1.2}>
        {displayedItems.map((item) => {
          if (!canViewAlert(item)) return null;

          const read = isInboxItemRead(item, uid);
          const created = itemCreatedDate(item);
          const dismissed = !isInboxItemAnnouncement(item) && isAlertDismissed(item, uid);
          const isAnnouncement = isInboxItemAnnouncement(item);
          return (
            <Card
              key={item.id}
              variant="outlined"
              role="button"
              tabIndex={0}
              onClick={() => handleRowOpen(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRowOpen(item);
                }
              }}
              sx={{
                borderLeftWidth: 3,
                borderLeftStyle: 'solid',
                borderLeftColor: read ? 'divider' : 'warning.main',
                bgcolor: read ? 'background.paper' : 'action.hover',
                cursor: 'pointer',
              }}
            >
              <CardContent sx={{ py: cardContentPy, '&:last-child': { pb: cardContentPy } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                      <Typography variant={isMenu ? 'body2' : 'subtitle2'} sx={{ fontWeight: 600 }}>
                        {item.title || 'Notification'}
                      </Typography>
                      {isAnnouncement ? (
                        <Chip size="small" label="Broadcast" color="primary" variant="outlined" icon={<CampaignIcon />} />
                      ) : (
                        <Chip size="small" label={item.severity || 'info'} color={severityColor(item.severity)} />
                      )}
                      {!read && <Chip size="small" label="Unread" color="warning" variant="outlined" />}
                      {dismissed && (
                        <Chip size="small" label="Archived" variant="outlined" icon={<ArchiveOutlinedIcon />} />
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={isMenu ? { fontSize: 13 } : undefined}>
                      {item.message || 'No message provided.'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {created ? created.toLocaleString() : '—'}
                      {isAnnouncement ? ' · Company broadcast' : ` · by ${item.createdByEmail || 'Super Admin'}`}
                    </Typography>
                  </Box>
                  {uid && !isAnnouncement && (
                    <IconButton
                      size="small"
                      aria-label="Alert actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        openRowMenu(e, item);
                      }}
                    >
                      <MoreVertIcon fontSize={isMenu ? 'small' : 'medium'} />
                    </IconButton>
                  )}
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor && menuItem)} onClose={closeMenu}>
        {menuItem && isInboxItemAnnouncement(menuItem) && !isInboxItemRead(menuItem, uid) && (
          <MenuItem onClick={() => markRead(menuItem.id)}>
            <ListItemIcon>
              <MarkEmailReadIcon fontSize="small" />
            </ListItemIcon>
            Mark as read
          </MenuItem>
        )}
        {menuItem && !isInboxItemAnnouncement(menuItem) && canOpenLinkedTicket(userData, menuItem, checkPermission) && (
          <MenuItem onClick={() => openLinkedTicket(menuItem)}>
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            Open ticket
          </MenuItem>
        )}
        {menuItem && !isInboxItemAnnouncement(menuItem) && !isAlertDismissed(menuItem, uid) && (
          <>
            {!isInboxItemRead(menuItem, uid) ? (
              <MenuItem onClick={() => markRead(menuItem.id)}>
                <ListItemIcon>
                  <MarkEmailReadIcon fontSize="small" />
                </ListItemIcon>
                Mark as read
              </MenuItem>
            ) : (
              <MenuItem onClick={() => markUnread(menuItem.id)}>
                <ListItemIcon>
                  <MarkEmailUnreadIcon fontSize="small" />
                </ListItemIcon>
                Mark as unread
              </MenuItem>
            )}
            <MenuItem onClick={() => dismissOne(menuItem.id)}>
              <ListItemIcon>
                <ArchiveOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Archive (clear from inbox)
            </MenuItem>
          </>
        )}
        {menuItem && !isInboxItemAnnouncement(menuItem) && isAlertDismissed(menuItem, uid) && (
          <MenuItem onClick={() => restoreOne(menuItem.id)}>
            <ListItemIcon>
              <UnarchiveIcon fontSize="small" />
            </ListItemIcon>
            Restore to inbox
          </MenuItem>
        )}
        {menuItem && !isInboxItemAnnouncement(menuItem) && canHardDeleteAlert(userData, menuItem) && (
          <MenuItem
            onClick={() => {
              const t = menuItem;
              setDeleteTarget(t);
              setConfirmDeleteOpen(true);
              closeMenu();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteForeverIcon fontSize="small" color="error" />
            </ListItemIcon>
            Delete permanently
          </MenuItem>
        )}
      </Menu>

      <Dialog open={confirmClearOpen} onClose={() => !bulkWorking && setConfirmClearOpen(false)}>
        <DialogTitle>Clear inbox?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All {inboxRows.length} notification{inboxRows.length === 1 ? '' : 's'} in your inbox will be moved to Archived.
            You can restore them later from the Archived tab.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)} disabled={bulkWorking}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={dismissAllInbox} disabled={bulkWorking}>
            {bulkWorking ? 'Working…' : 'Clear inbox'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRestoreAllOpen} onClose={() => !bulkWorking && setConfirmRestoreAllOpen(false)}>
        <DialogTitle>Restore all archived notifications?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {archivedRows.length} archived notification{archivedRows.length === 1 ? '' : 's'} will return to your inbox.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRestoreAllOpen(false)} disabled={bulkWorking}>Cancel</Button>
          <Button variant="contained" onClick={restoreAllArchived} disabled={bulkWorking}>
            {bulkWorking ? 'Working…' : 'Restore all'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteOpen} onClose={() => !deleteWorking && setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete this notification permanently?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the notification for everyone in the company. Archived copies and read state are discarded. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDeleteOpen(false); setDeleteTarget(null); }} disabled={deleteWorking}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmPermanentDelete} disabled={deleteWorking}>
            {deleteWorking ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDeleteAllArchivedOpen} onClose={() => !bulkWorking && setConfirmDeleteAllArchivedOpen(false)}>
        <DialogTitle>Purge {deletableArchivedRows.length} archived notification(s)?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Only notifications you are allowed to delete (company-targeted for admins, or any for Super Admin) will be removed.
            This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAllArchivedOpen(false)} disabled={bulkWorking}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteAllArchivedPermanent} disabled={bulkWorking}>
            {bulkWorking ? 'Working…' : 'Purge'}
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationMessageModal
        open={Boolean(messageModalItem) && !onOpenMessageModal}
        item={messageModalItem}
        createdAt={messageModalItem ? itemCreatedDate(messageModalItem) : null}
        onClose={() => setMessageModalItem(null)}
      />
    </Box>
  );
}
